<template>
    <RequirementAccordion
        ref="requirementAccordion"
        class="RiskRequirement"
        :title="title"
        :expanded="expanded"
        :requirement-type="requirementType"
        :left-strip-accent-status="isRequirementTypeInEvaluationRule ? 'success' : undefined"
        @update:expanded="$emit('update:expanded', $event)"
        @show="$emit('show')"
        @hide="$emit('hide')"
    >
        <template #afterTitle>
            <EvaluationRuleBadge
                v-if="isRequirementTypeInEvaluationRule"
                class="RiskRequirement__evaluationRuleIcon"
            />
            <div
                v-if="showValidationErrors && hasErrors"
                class="RiskRequirement__requiredWarning"
                :class="'RiskRequirement__requiredWarning--danger'"
            >
                <EidIcon
                    :icon-src="faExclamationTriangle"
                    :icon-props="{ style: { width: '16px', height: '16px' } }"
                />
            </div>
            <div
                v-if="hasWarnings"
                class="RiskRequirement__requiredWarning"
                :class="'RiskRequirement__requiredWarning--warning'"
            >
                <EidIcon
                    :icon-src="faExclamationTriangle"
                    :icon-props="{ style: { width: '16px', height: '16px' } }"
                />
            </div>
        </template>
        <template #beforeExpandToggle>
            <div class="RiskRequirement__criteriaCount">
                <strong>{{ criteriaCount }}</strong>
                <span> Criteria</span>
            </div>
        </template>
        <template #afterExpandToggle>
            <div class="RiskRequirement__deleteIconContainer">
                <NewButton
                    v-if="canDeleteRequirementType"
                    variant="flat"
                    color="neutral"
                    size="icon-sm"
                    class="RiskRequirement__deleteIcon"
                    aria-label="delete"
                    @click="onDelete"
                >
                    <EidIcon :icon-src="faXmark" />
                </NewButton>
            </div>
        </template>
        <template #content>
            <RiskRequirementCriterionAdvancedSettings
                v-if="currentCriterion"
                :title="title"
                :current-criterion="currentCriterion"
                :templates-by-field-name="templatesByFieldName"
                :custom-properties="customProperties"
                :country-code="countryCode"
                :criteria-list="criteriaList"
                :warning-message="getAdvancedSettingsWarningMessage(currentCriterion)"
                :show-validation-errors="showValidationErrors"
                :can-manage-criteria="canManageCriteria"
                @deselect-criterion="deselectCriterion"
                @criteria-input="onCriteriaInput"
                @reference-accessed="$emit('reference-accessed', $event)"
            />
            <RiskRequirementCriteria
                v-else
                :requirement-type="requirementType"
                :has-deprecated-criteria="hasDeprecatedCriteria"
                :criteria-list="criteriaList"
                :templates-by-field-name="templatesByFieldName"
                :custom-properties="customProperties"
                :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
                :criteria-templates="criteriaTemplates"
                :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
                :show-validation-errors="showValidationErrors"
                :can-manage-criteria="canManageCriteria"
                :can-open-criterion-settings="canOpenCriterionSettings"
                @open-settings="openCriterionSettings"
                @criteria-input="onCriteriaInput"
                @reference-accessed="$emit('reference-accessed', $event)"
                @validity-change="$emit('validityChange', $event)"
                @add-criterion="addCriterion"
            />
        </template>
    </RequirementAccordion>
</template>

<style lang="scss">
.RiskRequirement {
    &:hover {
        .RiskRequirement__deleteIcon {
            opacity: 1;
            visibility: visible;
        }
    }

    &__requiredWarning {
        display: flex;
        align-items: center;
        gap: 4px;

        &--danger {
            svg {
                color: var(--eidDangerRed);
            }
        }

        &--warning {
            svg {
                color: var(--eidWarningYellow);
            }
        }
    }

    &__requiredText {
        font-size: 11px;
        font-weight: 600;
        color: var(--eidDangerRed);
    }

    &__criteriaCount {
        font-size: 13px;
        color: var(--eidSecureBlue-75);
        white-space: nowrap;

        strong {
            font-weight: 500;
        }
    }

    &__deleteIconContainer {
        display: flex;
        align-items: center;
        // Stack above Quasar's .q-focus-helper hover overlay so the button isn't dimmed
        position: relative;
        z-index: 1;
    }

    &__deleteIcon {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
    }
}
</style>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { mapValues } from 'lodash';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import RequirementAccordion from '@/components/requirement-acoordion/RequirementAccordion.vue';
    import { IsReferenceStartsWithIsInvalid } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { castValueToMatchSchema } from '@/modules/decisioning-criteria/utils/syncCriteriaWithTemplates';
    import EvaluationRuleBadge from '@/modules/entity-details/components/EvaluationRuleBadge/EvaluationRuleBadge.vue';

    import RiskRequirementCriteria from './sub-components/RiskRequirementCriteria.vue';
    import RiskRequirementCriterionAdvancedSettings from './sub-components/RiskRequirementCriterionAdvancedSettings.vue';

    @Component({
        emits: [ 'criteriaInput', 'delete', 'hide', 'reference-accessed', 'show', 'update:expanded', 'validityChange' ],
        components: {
            EidIcon,
            NewButton,
            RequirementAccordion,
            EvaluationRuleBadge,
            RiskRequirementCriteria,
            RiskRequirementCriterionAdvancedSettings,
        },
    })
    export default class RiskRequirement extends Vue {
        @Prop()
        private requirementType!: TprmRequirementType;

        @Prop({ type: String, default: '' })
        private title!: string;

        @Prop({ type: Number, default: 0 })
        private criteriaCount!: number;

        @Prop({ type: Boolean, default: false })
        private expanded!: boolean;

        @Prop({ type: Boolean, default: false })
        private hasDeprecatedCriteria!: boolean;

        @Prop({ type: Boolean, default: false })
        private isRequirementTypeInEvaluationRule!: boolean;

        @Prop({ type: Boolean, default: false })
        private hasInvalidReferences!: boolean;

        @Prop({ type: Array, default: () => [] })
        private criteriaList!: EditableRequirementCriterion[];

        @Prop({ type: Object, default: () => ({}) })
        private templatesByFieldName!: Record<string, CriterionTemplate>;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Function as PropType<IsReferenceStartsWithIsInvalid>, default: () => () => false })
        private isReferenceStartsWithIsInvalid!: IsReferenceStartsWithIsInvalid;

        @Prop({ type: String })
        private countryCode?: string;

        @Prop({ type: Array, default: () => [] })
        private criteriaTemplates!: CriterionTemplate[];

        @Prop({ type: Array, default: () => [] })
        private evaluationRuleRequirementTypes!: TprmRequirementType[];

        @Prop({ type: Boolean, default: false })
        private showValidationErrors!: boolean;

        @Prop({ type: Boolean, default: true })
        private canDeleteRequirementType!: boolean;

        @Prop({ type: Boolean, default: true })
        private canManageCriteria!: boolean;

        @Prop({ type: Boolean, default: true })
        private canOpenCriterionSettings!: boolean;

        private faXmark = faXmark;
        private faExclamationTriangle = faExclamationTriangle;

        private currentCriterion: EditableRequirementCriterion | null = null;

        private get isInvalidCriteriaMessage(): boolean {
            return this.isReferenceStartsWithIsInvalid(this.requirementType);
        }

        private get hasErrors(): boolean {
            return this.hasInvalidReferences || this.isInvalidCriteriaMessage;
        }

        private get hasWarnings(): boolean {
            return this.hasDeprecatedCriteria;
        }

        private getAdvancedSettingsWarningMessage(criterion: EditableRequirementCriterion | null): string {
            const criterionTemplate = criterion
                ? this.templatesByFieldName[criterion.field]
                : null;

            if (this.hasDeprecatedCriteria && criterionTemplate?.deprecated) {
                return 'This criterion has been deprecated and may result in incorrect entity compliance.';
            }
            return '';
        }

        @Watch('criteriaList', { immediate: true, deep: true })
        private onCriteriaListChange(newValue: EditableRequirementCriterion[]) {
            if (this.currentCriterion) {
                const updatedCriterion = newValue.find((criterion) =>
                    criterion.coverageType === this.currentCriterion?.coverageType &&
                    criterion.field === this.currentCriterion.field,
                );
                if (updatedCriterion) {
                    this.currentCriterion = { ...updatedCriterion };
                }
            }
        }

        private onDelete(clickEvent: MouseEvent): void {
            clickEvent.stopPropagation();
            this.$emit('delete', this.requirementType);
        }

        private openCriterionSettings(criterion: EditableRequirementCriterion): void {
            this.currentCriterion = { ...criterion };
            this.$nextTick(() => {
                const requirementAccordion =
                    this.$refs.requirementAccordion as InstanceType<typeof RequirementAccordion> | undefined;
                if (requirementAccordion && requirementAccordion.$el) {
                    requirementAccordion.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    requirementAccordion.triggerHighlightPulse();
                }
            });
        }

        private deselectCriterion(): void {
            this.currentCriterion = null;
        }

        private onCriteriaInput(criteria: EditableRequirementCriterion[]): void {
            this.$emit('criteriaInput', criteria);
        }

        private addCriterion(template: CriterionTemplate): void {
            const newCriterion: EditableRequirementCriterion = {
                coverageType: template.coverageType,
                field: template.field,
                references: mapValues(
                    template.evaluator.references,
                    (ref) => castValueToMatchSchema(ref.value, ref),
                ),
                verify: template.verify,
                emailMessage: template.emailMessage,
                deprecated: template.deprecated,
            };
            this.$emit('criteriaInput', [ ...this.criteriaList, newCriterion ]);
        }
    }
</script>
